/**
 * Dashboard filters (screenshots/84: Since · Topic · Wards) applied to the data widgets' queries.
 * Pure and DOM-free: the Worker applies the same function before it runs the first-paint queries, so the
 * client's query URLs match the snapshot's and nothing re-fetches on first paint.
 *
 * - Since → the dataset's date field `gte` the date (contributions/roll calls/meetings `date`, filings `quarter`,
 *   legislation `introduced`).
 * - Topic → `topic is` on datasets that have a topic field.
 * - Wards → `ward in` on datasets that have a ward field.
 * A dataset without the field ignores that filter (the coverage note says so, `filterSummary`).
 */
import { DATASETS, type DataBinding, type DatasetId, type Filter } from "../overview/charts/datasets";
import type { DashboardFilterKey } from "../overview/widgets/registry";

export type PageFilters = Partial<Record<DashboardFilterKey, string | string[]>>;

const DATE_FIELD: Partial<Record<DatasetId, string>> = {
  contributions: "date", rollCalls: "date", lobbyingFilings: "quarter", meetings: "date", legislation: "introduced", wardResponses: "date", testimony: "date",
};
const has = (id: DatasetId, field: string) => !!DATASETS[id]?.fields.some((f) => f.id === field);

/** The filters that are set ("Wards: All" = not set). */
export function activeFilters(f: PageFilters): DashboardFilterKey[] {
  return (Object.keys(f) as DashboardFilterKey[]).filter((k) => {
    const v = f[k];
    return Array.isArray(v) ? v.length > 0 : typeof v === "string" && v !== "";
  });
}

/** The page's filters as dataset filter clauses for one binding (only the ones its dataset has). */
export function pageClauses(datasetId: DatasetId, f: PageFilters): Filter[] {
  const out: Filter[] = [];
  const since = typeof f.since === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f.since) ? f.since : null;
  const date = DATE_FIELD[datasetId];
  if (since && date && has(datasetId, date)) out.push({ field: date, op: "gte", value: since });
  if (typeof f.topic === "string" && f.topic && has(datasetId, "topic")) out.push({ field: "topic", op: "is", value: f.topic });
  const wards = (Array.isArray(f.wards) ? f.wards : typeof f.wards === "string" && f.wards ? [f.wards] : []).filter((w) => /^\d{1,2}$/.test(w));
  if (wards.length && has(datasetId, "ward")) out.push({ field: "ward", op: "in", value: wards });
  return out;
}

/** A binding with the page's filters appended (after the widget's own filters). */
export function withPageFilters(b: DataBinding, f: PageFilters | null | undefined): DataBinding {
  if (!f) return b;
  const extra = pageClauses(b.datasetId, f);
  return extra.length ? { ...b, filters: [...b.filters, ...extra] } : b;
}

/** Layout items with the page filters baked into their chart settings (Worker first paint; print appendix). */
export function itemsWithPageFilters<T extends { config?: Record<string, unknown> | null }>(items: T[], f: PageFilters | null | undefined): T[] {
  if (!f || !activeFilters(f).length) return items;
  return items.map((i) => {
    const s = i.config?.settings as { data?: DataBinding } | undefined;
    if (!s?.data) return i;
    return { ...i, config: { ...i.config, settings: { ...s, data: withPageFilters(s.data, f) } } };
  });
}

/** Chip text: "Since Jan 1, 2025" · "Topic: Housing" · "Wards: All" (84). */
export function filterChip(k: DashboardFilterKey, f: PageFilters): string {
  const v = f[k];
  if (k === "since") {
    if (typeof v !== "string" || !v) return "Since: All";
    const d = new Date(`${v}T12:00:00Z`);
    return `Since ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}`;
  }
  if (k === "topic") return `Topic: ${typeof v === "string" && v ? v : "All"}`;
  const ws = Array.isArray(v) ? v : typeof v === "string" && v ? [v] : [];
  return `Wards: ${ws.length ? ws.join(", ") : "All"}`;
}
